'use client';

import { CloseBigIcon, DropdownIcon, PinIcon, RefreshIcon } from '@/components/icons/svg-icons';
import { Button, Dropdown, DropdownEvent, DropdownOption } from '@nf/ui';
import { useAccountInfo } from '@/hooks/query/account';
import { MatchModelUnion, SportMatchModel } from '@nf/types/odds';
import { ChannelModel, EsportChannelModel, streamingService } from '@/services/streaming.service';
import { StatisticButton } from '@/components/feature-buttons/statistic-button';
import { useAuthToken } from '@/store/auth';
import { StreamingType, useStreamingStore } from '@/store/streaming';
import { useTranslations } from 'next-intl';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { getTeamName } from '@/utils/odds-utils';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { esportOptionLangMapping } from '@/settings/esport-setting';
import { setKafkaNormal, setKafkaStreaming } from '@/hooks/query/use-kafka-log';
import { useCurrentProperties } from '@/hooks/query';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Styles } from './streaming-bar.style';
import { cx } from 'styled-system/css';
import { KafkaNote, StreamingKafkaNote } from '@nf/utils-common/constants';
import { useParams } from 'next/navigation';
import { getKafkaStreamerType } from '@/utils/common-util';
import {
	InHouseStreamingUrlWithSecretResponse,
	useGetInHouseStreamingUrlWithSecret
} from '@/hooks/query/use-streaming-data';
import { useAnalyseAttribute } from '@/features/next-analyse';
import { useSlmtGv } from '@/hooks/query/use-slmt-gv';
import { MaybeMatch } from '@nf/utils-common/fpw/monadic';
import { SingleMatchGoldenDuelPool } from '@/features/promotion/golden-duel/components/single-match-golden-duel-pool/single-match-golden-duel-pool';
import { useGoldenDuelSetting } from '@/hooks/query/golden-duel';

interface StreamingBarProps {
	match: SportMatchModel;
	isStreamingBar: boolean;
	home: string;
	away: string;
	isAsideLayout: boolean;
	isStreamingPlugin?: boolean;
};

const useChannelModel = (): ChannelModel[] => {
	const [
		channelList,
	] = useStreamingStore(state => [
		state.channelList,
	]);
	const { user } = useAccountInfo();
	const [inHouseIndex, setInHouseIndex] = useState<number>(channelList.findIndex(channelModel => channelModel.value === '13'));
	const [inHouseSourceId, setInHouseSourceId] = useState('');
	const { refetch } = useGetInHouseStreamingUrlWithSecret(
		inHouseIndex === -1 ? '' : inHouseSourceId ?? '',
		user?.ApiBackendUrl || '',
		user?.AccessToken || ''
	);
	const [inHouseOtherSource, setInHouseOtherSource] = useState([] as ChannelModel[]);

	useEffect(() => {
		const index = channelList.findIndex(channelModel => channelModel.value === '13')
		setInHouseIndex(index);

		const sourceId = index > -1
			? channelList[index].sid
			: '';

		setInHouseSourceId(sourceId);
	}, [channelList])

	useEffect(() => {

		const index = channelList.findIndex(x => x.value == '13');
		const sourceId = index === -1 ? '' : channelList[index];
		if (index === -1 || sourceId === '') {
			setInHouseOtherSource([] as ChannelModel[]);
			return;
		}

		refetch().then((response) => {
			const inHouseStreamingUrlGroups: InHouseStreamingUrlWithSecretResponse[] = response.data?.data?.Data || [];
			if (inHouseStreamingUrlGroups.length <= 1) {
				setInHouseOtherSource([] as ChannelModel[])
				return;
			}

			const inHouseSource = channelList[index];
			const inHouseOtherSource: ChannelModel[] = [] as ChannelModel[];
			for (let index = 1; index < inHouseStreamingUrlGroups.length; index++) {
				inHouseOtherSource.push({
					label: `${inHouseSource.label}-${index}`,
					value: `${inHouseSource.value}-${index}`,
					sid: inHouseSource.sid,
					fixing: inHouseSource.fixing,
					type: inHouseSource.type,
					siteId: inHouseSource.siteId,
				});
			}
			setInHouseOtherSource(inHouseOtherSource);
		})

	}, [inHouseIndex, inHouseSourceId]);

	return inHouseOtherSource.length > 0
		? channelList.length === 1
			? [...channelList, ...inHouseOtherSource]
			: [
				...channelList.slice(0, inHouseIndex + 1),
				...inHouseOtherSource,
				...channelList.slice(inHouseIndex + 1)
			]
		: channelList;
}

const ChannelListDropdown = ({ match, userLang }: { match: SportMatchModel, userLang: string }) => {
	const [
		streamingType,
		setSwitchChannel,
		setIsLoading,
		isEsport,
		esportSrc,
		esportchannelList,
		setEsportSrc,
		streamingSrc,
	] = useStreamingStore(state => [
		state.streamingType,
		state.setSwitchChannel,
		state.setIsLoading,
		state.isEsport,
		state.esportSrc,
		state.esportchannelList,
		state.setEsportSrc,
		state.streamingSrc,
		state.channelList,
	]);
	const [dropdownOptions, setDropdownOptions] = useState<ChannelModel[] | EsportChannelModel[]>([]);
	const [selectedDropdownOption, setSelectedDropdownOption] = useState<string | undefined>(undefined);
	const channelModel = useChannelModel();

	useEffect(() => {
		const currentSelected = isEsport ? esportSrc : streamingSrc;
		if (currentSelected !== selectedDropdownOption) {
			setSelectedDropdownOption(currentSelected);
		}
	}, [streamingSrc, isEsport, esportSrc]);
	useEffect(() => {
		const models = isEsport ? esportchannelList : channelModel;
		if (dropdownOptions.length !== models.length
			|| models.some(incomingModel => dropdownOptions.every(option => option.value !== incomingModel.value || option.label !== incomingModel.label))) {
			setDropdownOptions(models);
		}
	}, [channelModel, isEsport, esportchannelList]);

	const switchChannel = (e: DropdownEvent) => {
		if (streamingSrc === e.option.value) {
			return;
		}
		// setkafka'
		if (e.option.value === 11 || e.option.value === 20) {
			const streamerKafkaData = {
				note: StreamingKafkaNote.SingleMatch,
				type: e.option.value === 11 ? 3 : (e.option.value === 19 ? 4 : 5),
				action: 1,
				matchId: 0,
				streamer_type: getKafkaStreamerType(0)
			};
			setKafkaStreaming(streamerKafkaData);
		} else if (streamingType === 'Streamer' && e.option.value) {
			let srcId: number = 0;
			if (e.option.value.indexOf('-') > -1) {
				srcId = Number(e.option.value.split('-')[0]);
			} else {
				srcId = Number(e.option.value);
			}
			let kafkaData;
			const streamerType = srcId ? srcId - 19 : 0
			const McdnStreamerType = srcId ? srcId - 23 : 0

			switch (srcId) {
				case 20:
				case 21:
				case 22:
				case 23:
					kafkaData = {
						type: 5,
						action: 1,
						note: StreamingKafkaNote.SingleMatch,
						matchId: match.MatchId,
						streamer_type: streamerType
					};
					setKafkaStreaming(kafkaData)
					break;
				case 24:
				case 25:
				case 26:
				case 27:
				case 28:
				case 29:
					kafkaData = {
						type: 5,
						action: 1,
						note: StreamingKafkaNote.SingleMatch,
						matchId: match.MatchId,
						streamer_type: (srcId === 28 || srcId === 29) ? 6 : McdnStreamerType
					};
					setKafkaStreaming(kafkaData)
					break;
				default:
					break;
			}
		}
		setIsLoading(true);
		setSwitchChannel(e.option.value);
	};

	const switchEsportChannel = (e: DropdownEvent) => {
		if (esportSrc === e.option.value) {
			return;
		}
		setEsportSrc(e.option.value);
	};
	const createEsportOption = (option: DropdownOption, esportchannelList: EsportChannelModel[]) => {
		if (!esportchannelList) {
			return;
		}
		let esportTextContenet = '';
		const currentOption: EsportChannelModel | undefined = esportchannelList.find((item) => {
			return item.value === option.value
		});
		const esportCurrentSetting = esportOptionLangMapping[currentOption?.lang as keyof typeof esportOptionLangMapping];
		esportTextContenet = esportCurrentSetting.fName
		if (userLang === 'en-US') {
			esportTextContenet = !esportCurrentSetting.fName2 ?
				esportCurrentSetting.fName
				: esportCurrentSetting.fName2;
		}

		return (
			<>
				{
					esportCurrentSetting.icon && <esportCurrentSetting.icon />
				}
				{
					esportTextContenet && <span className="dropdown__item-text">{`${esportTextContenet} ${option.label}`}</span>
				}
			</>
		);
	};

	return (
		<>
			{
				(dropdownOptions.length <= (isEsport ? 0 : 1)) ? null : (
					<Dropdown
						options={dropdownOptions.map((option): DropdownOption => {
							return {
								label: option.label,
								value: option.value,
							}
						})}
						value={selectedDropdownOption ?? dropdownOptions[0].value}
						onChange={isEsport ? switchEsportChannel : switchChannel}
						iconTemplate={() => <DropdownIcon />}
						optionTemplate={!isEsport ? undefined : (option) => createEsportOption(option, esportchannelList)}
					/>
				)
			}
		</>
	);
};
/**
 * [Galaxy / Mars] Match Panel > Streaming Bar
 * @description 此元件顯示於 width < 1250 的 Single Match 及 width >= 1250 的 Aside
 */
export const StreamingBar = ({ match, isStreamingBar, home, away, isAsideLayout, isStreamingPlugin }: StreamingBarProps) => {
	const { eventMode, eventName, routePrefix, sport, market, leagueGroupId } = useCurrentProperties();
	const t = useTranslations('General');
	const oddsData = useAtomValue(oddsPageAtom);
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const authToken = useAuthToken();
	const { hasVideo, hasGV, hasStreamer, hasLSports } = streamingService.getVideoOptions(match as MatchModelUnion, false, SiteConfigsData, user);
	const [
		streamingType,
		setStreamingType,
		streamingPluginUrl,
		setStreamingPluginUrl,
		setChangeMatchStatus,
		setStreamingUrl,
		isClose,
		setIsClose,
		isRefresh,
		setIsRefresh,
		isPin,
		setIsPin,
		isEsport,
		setIsLoading
	] = useStreamingStore(
		state => [
			state.streamingType,
			state.setStreamingType,
			state.streamingPluginUrl,
			state.setStreamingPluginUrl,
			state.setChangeMatchStatus,
			state.setStreamingUrl,
			state.isClose,
			state.setIsClose,
			state.isRefresh,
			state.setIsRefresh,
			state.isPin,
			state.setIsPin,
			state.isEsport,
			state.setIsLoading
		],
	);

	// plugin type 1 or 2 need remove icon
	const invisibleIcon = isStreamingPlugin && !!streamingPluginUrl && streamingType && match?.GameID !== 50;

	const { MatchId, GameID, LeagueId } = match;
	const params = useParams();
	const { hasSlmtGv } = useSlmtGv({ match, sport: params.sport });
	const view = useAppearanceStore(state => state.view);
	const isSingleMatchPage = params.matchId !== undefined;
	const { isGoldenDuelSupported } = useGoldenDuelSetting();
	const isGoldenDuel = isGoldenDuelSupported(sport, LeagueId ?? 0);
	const setKafkaGalaxy = (name: StreamingType) => {
		if (!isSingleMatchPage) return; // 於 aside 點擊是否記錄, 以是否在 single match 頁為準
		let action;
		switch (name) {
			case 'Streamer':
				action = 4;
				break;
			case 'Streaming':
				action = 5;
				break;
			case 'GV':
				action = 6;
				break;
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.GalaxyV2,
				type: 9,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const setKafkaMars = (name: StreamingType) => {
		let action;
		switch (name) {
			case 'Streamer':
				action = isAsideLayout ? 18 : 27;
				break;
			case 'Streaming':
				action = isAsideLayout ? 19 : 26;
				break;
			case 'GV':
				action = isAsideLayout ? 20 : 25;
				break;
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 6,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const switchStreamingType = (name: StreamingType, isAutoOpenStreamer: boolean = false) => {
		if (streamingType === name) {
			return;
		}
		// 為了讓比賽關閉時plugin還可以切去主播，不讓streamingPluginUrl 變空值，但又必須清掉chatroom url
		if (!isAutoOpenStreamer) {
			streamingPluginUrl && setStreamingPluginUrl('clear');

			if (view === ViewEnum.galaxy) {
				setKafkaGalaxy(name);
			} else {
				setKafkaMars(name);
			}
		}

		const streamerKafkaData = {
			note: isSingleMatchPage ? StreamingKafkaNote.SingleMatch : StreamingKafkaNote.OddsPage,
			type: name === 'Streamer' ? 13 : 11,
			action: 1,
			matchId: MatchId || 0,
			streamer_type: getKafkaStreamerType((hasStreamer > 0) ? hasStreamer : 0)
		};
		setKafkaStreaming(streamerKafkaData);

		setStreamingType(name);
		setChangeMatchStatus([t('lbl_Streamer'), t.raw('lbl_Source')], match as SportMatchModel, false, SiteConfigsData, user);
	};

	const close = () => {
		streamingPluginUrl && setStreamingPluginUrl('');
		const streamerKafkaData = {
			note: StreamingKafkaNote.SingleMatch,
			type: streamingType === 'Streamer' ? 11 : 9,
			action: 2,
			matchId: MatchId || 0,
			streamer_type: getKafkaStreamerType(0)
		};
		setKafkaStreaming(streamerKafkaData);
		setIsClose(!isClose);
		setStreamingType('');
		setStreamingUrl('');
	}

	const refresh = () => {
		setIsRefresh(!isRefresh);
		setTimeout(() => {
			setIsRefresh(false);
		}, 1000);
	}

	const getCricketStaticInfo = () => {
		if (match?.GameID === 50 && streamingType === 'StaticInfo' && !(getTeamName(match?.TeamId1, oddsData?.teamNameMap).includes('(V)') && getTeamName(match?.TeamId2, oddsData?.teamNameMap).includes('(V)'))) {
			return false;
		}
		return true;
	}

	const getStreamingBarList = () => {
		const streamingBarList = [];
		(hasStreamer && !invisibleIcon && match.IsLive) && streamingBarList.push({ icon: <i className="symbol symbol--streamer"></i>, Name: 'Streamer', Translation: t('lbl_Streamer') });
		(hasVideo && !invisibleIcon) && streamingBarList.push({ icon: <i className="symbol symbol--video"></i>, Name: 'Streaming', Translation: t('lbl_Strm') });
		(!invisibleIcon && hasSlmtGv && match.IsLive) && streamingBarList.push({ icon: <i className="symbol symbol--gv"></i>, Name: 'SlmtGV', Translation: t('LiveMatch') });
		// hasLSports && streamingBarList.push({ icon: <i className="symbol symbol--gv"></i>, Name: 'GV', Translation: t('LiveMatch') });
		return streamingBarList;
	}
	const StreamingTypeSwitch = ({ analyseKey, isActive, clickHandler, children }: { analyseKey: string, isActive: boolean, clickHandler: () => void, children: ReactNode }) => {
		const taggedElement = useAnalyseAttribute(analyseKey);
		return (
			<Button
				variant="icon"
				onClick={clickHandler}
				data-active={isActive}
				ref={taggedElement}
			>
				{children}
			</Button>
		);
	};

	// NOTE: 無任何 streaming 按鈕時，隱藏整個 streaming bar
	const streamingBarRef = useRef<HTMLDivElement>(null);
	const [isHiddenStreamingBar, setIsHiddenStreamingBar] = useState(false);
	useEffect(() => {
		const parentDiv = streamingBarRef.current;
		if (parentDiv) {
			const childDivs = parentDiv.querySelectorAll('div');
			let allChildEmpty = true;
			childDivs.forEach((childDiv) => {
				if (childDiv.childNodes.length !== 0) {
					allChildEmpty = false;
				}
			});
			setIsHiddenStreamingBar(allChildEmpty);
		}
	}, [hasStreamer, hasVideo, hasGV, hasLSports, invisibleIcon]);

	useEffect(() => {
		if (isAsideLayout) {
			setIsClose(true);
			setStreamingType('');
			setStreamingUrl('');
		}
	}, [eventMode, eventName, routePrefix, sport, market, leagueGroupId]);


	// 若有開啟siteConfig則有streamer預設開啟streamer
	useEffect(() => {
		if (user?.ActStatus === 2 && SiteConfigsData?.EnableDefaultOpenStreamer) {
			MaybeMatch(params.matchId && isSingleMatchPage && hasStreamer !== 0 && match.IsLive)
				.fold(
					() => {
						setStreamingType('');
					},
					() => {
						switchStreamingType('Streamer', true);
					})
		}
	}, [SiteConfigsData?.EnableDefaultOpenStreamer, hasStreamer, isSingleMatchPage, match.IsLive, params.matchId, user?.ActStatus]);

	useEffect(() => {
		setIsLoading(true);

		return () => {
			setIsLoading(false);
		};
	}, []);

	const showChannelList = isStreamingBar && match.IsLive && streamingType !== 'GV' && streamingType !== 'StaticInfo' && (isEsport || !isStreamingPlugin);
	return (
		// <div className={cx('video-bar', Styles.videoBar)}>
		<div className={cx(`video-bar${isStreamingPlugin ? ' video-bar--useplugin' : ''}`, Styles.videoBar, isHiddenStreamingBar && Styles.hidden)} ref={streamingBarRef}>
			{
				isGoldenDuel && isStreamingBar ?
					<SingleMatchGoldenDuelPool matchId={match.MatchId} parentMatchId={match.Parentmatchid} isVideoBar={true} />
					: null
			}
			<div className="video__col">
				{
					!showChannelList ? null : (
						<ChannelListDropdown
							match={match}
							userLang={user?.Lang || 'en-US'}
						/>
					)
				}
			</div>
			<div className="video__col video__col--middle">
				{
					authToken ? (
						getStreamingBarList().map((item, i) => (
							<StreamingTypeSwitch
								key={item.Name}
								analyseKey={item.Name === 'Streaming' ? 'use-streaming-from-single-match' : item.Name === 'Streamer' ? 'use-streamer-from-single-match' : ''}
								clickHandler={() => switchStreamingType(item.Name as StreamingType)}
								isActive={streamingType === item.Name}
							>
								{item.icon}
								{view === ViewEnum.mars ? (
									<span className="btn__text">{item.Translation}</span>
								) : null}
							</StreamingTypeSwitch>
						))) : null
				}
				{
					GameID === 50 ? (
						<StatisticButton
							isShowText={view === ViewEnum.mars ? 'true' : undefined}
							match={match}
							isNeedSetMatchId={false}
							home={home}
							away={away}
							active={streamingType === 'StaticInfo'}
							onClick={() => {
								switchStreamingType('StaticInfo');
							}}
						/>
					) : null
				}
			</div>
			<div className="video__col video__col--right">
				{isStreamingBar ?
					(
						<>
							{!invisibleIcon && !SiteConfigsData?.EnableStreamingPlugin ?
								<Button
									variant="icon"
									data-active={isRefresh}
									onClick={refresh}
								>
									<RefreshIcon />
								</Button> : null}
							{
								!isAsideLayout ?
									<Button
										variant="icon"
										className="btn--pin"
										data-active={isPin}
										onClick={() => setIsPin(!isPin)}
									>
										<PinIcon />
									</Button> : null
							}
							<Button
								variant="icon"
								data-active={isClose}
								onClick={close}
							>
								<CloseBigIcon />
							</Button>
						</>
					) : GameID !== 50 && view === ViewEnum.galaxy ? (
						<StatisticButton
							match={match}
							isNeedSetMatchId={false}
							home={home}
							away={away}
						/>
					) : null}
			</div>
		</div>
	);
};
